<template>
  <!-- THE ANSWER FIRST. Today opens on the ranked list, with the ask said in one
       plain line above it and the handful of sports a punter most often wants
       one tap away. Everything else about the ask (games, locked legs, shape,
       book, a strategy) is in the builder sheet behind "Change".
       docs/tips-ux.md is the state machine. -->
  <section class="ask" aria-label="Your multis">
    <div class="sheet ask-card">
      <div class="ask-line">
        <p class="ask-said">{{ summary }}</p>
        <button ref="changeBtn" type="button" class="tbtn" aria-haspopup="dialog" @click="openBuilder">Change</button>
      </div>
      <p v-if="beforeDrop" class="ask-note">Sports prices open at {{ dropTime }}. Racing is on now.</p>
      <div v-if="quickSports.length" class="capsules" role="group" aria-label="Quick sports">
        <button
          v-for="chip in quickSports"
          :key="chip.key"
          type="button"
          class="capsule"
          :class="{ 'capsule--off': stateOf(chip) === 'avoid' }"
          :aria-pressed="isOn(chip) ? 'true' : 'false'"
          @click="tapQuick(chip)"
        >{{ capsuleLabel(chip) }}</button>
        <button type="button" class="capsule capsule--more" aria-haspopup="dialog" @click="openBuilder">More</button>
      </div>
    </div>

    <!-- THE LIST HEADER: how many fit, their order, how fresh the prices are. -->
    <header class="list-head">
      <div class="list-top">
        <h2 class="count" aria-live="polite">{{ countLine }}</h2>
        <!-- NEVER THE WHOLE LIST: a picture of four hundred multis is a
             picture nobody reads. The top few, or a hand-picked few. -->
        <button
          v-if="canShare && shownCount && !selecting"
          type="button"
          class="tbtn"
          aria-haspopup="dialog"
          :disabled="sharing"
          @click="askingShare = true"
        >Share</button>
      </div>
      <Segmented
        v-if="!askView.legacy"
        :options="SORTS"
        :model-value="askSelections.strategy"
        label="Sort"
        @update:model-value="setSort"
      />
      <AskListTools v-if="!askView.legacy" />
      <p class="fresh" :class="{ 'is-stale': stale }">{{ stampLine }}</p>
    </header>

    <section v-if="askView.unsatisfied" class="sheet unsatisfied" aria-label="What to change">
      <p class="unsatisfied-why">{{ cap(askView.unsatisfied.reasons.join('; ')) }}.</p>
      <p class="unsatisfied-try">Try</p>
      <div class="loosens">
        <button
          v-for="item in askView.unsatisfied.loosen"
          :key="`${item.dimension}:${item.suggestion}`"
          type="button"
          class="capsule"
          @click="loosen(item)"
        >{{ cap(item.suggestion) }}</button>
      </div>
      <p v-if="askView.relaxed.length" class="unsatisfied-after">Meanwhile, the closest thing, loosened:</p>
    </section>

    <template v-if="askView.legacy">
      <p v-for="n in askView.legacy.notes" :key="n" class="foot-line centred">{{ n }}</p>
      <Ticket v-for="t in askView.legacy.tickets" :key="t.multi.id" :multi="t.multi" :legs="t.legs" :now="now" />
    </template>

    <ol class="answers">
      <template v-for="(batch, bi) in askView.batches" :key="batch.at + bi">
        <li v-for="t in batch.results" :key="t.multi.id">
          <AskTicket
            :ticket="t"
            :pinned="isPinned(t)"
            :swapping-key="swapping && swapping.ticket.multi.id === t.multi.id ? swapping.legKey : null"
            :swap="swapView"
            :now="now"
            :selectable="selecting"
            :selected="isChosen(t)"
            @toggle-select="toggleChosen"
            @share="shareOne"
            @swap="startSwap"
            @use-swap="useSwap"
            @cancel-swap="swapping = null"
            @lock="toggleLock"
            @pin="togglePin"
            @dismiss="dismiss"
            @place="emit('place', $event)"
            @copy="emit('copy', $event)"
          />
        </li>
      </template>
    </ol>

    <!-- CHOOSING: the bar rides the foot of the screen while the list scrolls
         under it. One filled action, and the way back out. -->
    <div v-if="selecting" class="select-bar" role="group" aria-label="Share the chosen multis">
      <p class="select-note" aria-live="polite">{{ chooseLine }}</p>
      <div class="select-actions">
        <button type="button" class="tbtn" @click="stopChoosing">Cancel</button>
        <button
          type="button"
          class="primary"
          :disabled="!chosen.length || sharing"
          @click="shareChosen"
        >Share {{ chosen.length }}</button>
      </div>
    </div>

    <!-- THE REST OF THE LIST, a page at a time as it is scrolled to. The button
         is the same action for anyone not scrolling (a keyboard, a switch). -->
    <div v-if="askView.batches.length" ref="sentinel" class="more">
      <button v-if="canMore" type="button" class="tbtn more-btn" @click="more()">Show {{ PAGE }} more</button>
      <p v-else-if="shownCount" class="foot-line centred">{{ endLine }}</p>
    </div>

    <section v-if="askPinned.length" class="kept" aria-labelledby="pinned-title">
      <h2 id="pinned-title" class="kept-head">Pinned</h2>
      <Ticket v-for="p in askPinned" :key="p.ticket.multi.id" :multi="p.ticket.multi" :legs="p.ticket.legs" :now="now" compact />
    </section>

    <section v-if="askSaved.length" class="kept" aria-labelledby="saved-title">
      <h2 id="saved-title" class="kept-head">Your daily asks</h2>
      <ul class="kept-rows">
        <li v-for="s in askSaved" :key="s.id" class="kept-row">
          <button type="button" class="tbtn kept-name" @click="loadSaved(s)">{{ s.name }}</button>
          <button type="button" class="tbtn tbtn--quiet" :aria-label="`Remove ${s.name}`" @click="removeSaved(s.id)">Remove</button>
        </li>
      </ul>
    </section>

    <section v-if="historyToday.length" class="kept" aria-labelledby="history-title">
      <h2 id="history-title" class="kept-head">Shown today</h2>
      <button type="button" class="tbtn" :aria-expanded="showHistory ? 'true' : 'false'" @click="showHistory = !showHistory">
        {{ showHistory ? 'Hide them' : `See all ${historyCount}` }}
      </button>
      <template v-if="showHistory">
        <div v-for="h in historyToday" :key="h.at + h.key" class="history-entry">
          <p class="foot-line">{{ clock(h.at) }} · {{ h.interpretation }}</p>
          <Ticket v-for="t in h.tickets" :key="t.multi.id + h.at" :multi="t.multi" :legs="t.legs" :now="now" compact />
        </div>
      </template>
    </section>

    <p class="how">
      <button type="button" class="tbtn" aria-haspopup="dialog" @click="emit('how')">How it works</button>
    </p>

    <!-- WHAT TO SHARE, asked once. The share runs inside the button's own
         handler, while the tap's activation is still live. -->
    <IonActionSheet
      :is-open="askingShare"
      header="Share multis"
      :buttons="shareButtons"
      @didDismiss="askingShare = false"
    />

    <!-- THE BUILDER SHEET. Its contents mount only while it is up. -->
    <IonModal
      :is-open="building"
      :breakpoints="[0, 0.92]"
      :initial-breakpoint="0.92"
      :handle="true"
      :expand-to-scroll="false"
      aria-label="Build a multi"
      @didDismiss="builderClosed"
    >
      <AskBuilder v-if="builderMounted" @close="building = false" />
    </IonModal>
  </section>
</template>

<script setup>
import {
  computed, defineAsyncComponent, nextTick, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { IonActionSheet, IonModal } from '@ionic/vue';
import Ticket from '../Ticket.vue';
import AskTicket from './AskTicket.vue';
import Segmented from './Segmented.vue';
import AskListTools from './AskListTools.vue';
import {
  askSelections, askPreset, askChips, askView, askPinned, askSaved, historyToday,
  setChip, loosen, another, viewed, swapOptions, applySwap, dismiss, togglePin, isPinned,
  loadSaved, removeSaved, hasAskBoard, toggleLock, askBooks, askBook, SORTS, setSort, PAGE, scheduleAsk,
} from '../../lib/stores/ask.js';
import { askSummary, matchCount } from '../../lib/ask/summary.js';
import { now, pricesFetchedAt, racingCard, snapshot } from '../../store.js';
import { STALE_AFTER_MIN } from '../../lib/format.js';
import { bookmakerName } from '../../lib/odds.js';

defineProps({
  /** Share is offered (a real board, not the sample one). */
  canShare: { type: Boolean, default: false },
  /** A share is being drawn. */
  sharing: { type: Boolean, default: false },
});
const emit = defineEmits(['place', 'copy', 'share-tickets', 'how']);

// Fetched when first opened, never with Today itself.
const AskBuilder = defineAsyncComponent(() => import('./AskBuilder.vue'));

// --- the ask, in words ----------------------------------------------------------------
const nameOf = (key, fallback) => {
  const known = bookmakerName(key);
  return known && known !== key ? known : fallback;
};
const bookName = computed(() => {
  const key = askBook.value;
  if (!key) return null;
  return nameOf(key, askBooks.value.find((b) => b.key === key)?.name ?? key);
});
const summary = computed(() => askSummary(askSelections, { preset: askPreset.value, bookName: bookName.value }));

// --- quick sports -------------------------------------------------------------------
// The four sports with the most still to start, then any other sport the ask
// already names, in the board's own order so nothing moves under the thumb.
const QUICK = 4;
const stateOf = (chip) => askSelections.chips[chip.key] ?? null;
const isOn = (chip) => ['include', 'only', 'prefer'].includes(stateOf(chip));
const quickSports = computed(() => {
  const sports = askChips.value.sports;
  const top = new Set(sports.filter((c) => c.value !== 'racing').slice()
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key)).slice(0, QUICK).map((c) => c.key));
  return sports.filter((c) => top.has(c.key) || stateOf(c));
});
const capsuleLabel = (chip) => {
  const s = stateOf(chip);
  if (s === 'only') return `Only ${chip.label}`;
  if (s === 'avoid') return `Not ${chip.label}`;
  return chip.label;
};
/** Plain on and off: on is Include. Anything else set in the sheet clears. */
function tapQuick(chip) {
  setChip(chip, stateOf(chip) ? null : 'include');
}

// --- the builder sheet ----------------------------------------------------------------
const building = ref(false);
const builderMounted = ref(false);
const changeBtn = ref(null);
function openBuilder() {
  builderMounted.value = true;
  building.value = true;
}
function builderClosed() {
  building.value = false;
  builderMounted.value = false;
  nextTick(() => changeBtn.value?.focus());
}

// --- the stamp ------------------------------------------------------------------------
const beforeDrop = computed(() => !pricesFetchedAt.value && !!snapshot.value?.dropsAt);
const clock = (iso) => new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }).replace(' ', '').toLowerCase();
const dropTime = computed(() => (snapshot.value?.dropsAt ? clock(snapshot.value.dropsAt) : '7am'));
const ageOf = (iso) => (iso ? Math.max(0, Math.round((now.value - Date.parse(iso)) / 60_000)) : null);
const agoWord = (m) => (m === null ? null : m < 1 ? 'just now' : m < 60 ? `${m} min ago` : `${Math.floor(m / 60)} h ${m % 60} min ago`);
const sportsAge = computed(() => ageOf(pricesFetchedAt.value));
const racingAge = computed(() => ageOf(racingCard.value?.fetchedAt ?? null));
const stale = computed(() => (sportsAge.value ?? 0) > STALE_AFTER_MIN || (racingAge.value ?? 0) > STALE_AFTER_MIN);
const stampLine = computed(() => {
  const s = sportsAge.value;
  const r = racingAge.value;
  let said;
  if (s !== null && r !== null && agoWord(s) !== agoWord(r)) said = `Prices ${agoWord(s)} · racing ${agoWord(r)}`;
  else if (s !== null) said = `Prices ${agoWord(s)}`;
  else if (r !== null) said = `Racing prices ${agoWord(r)}`;
  else return 'No prices on this device yet · pull to refresh';
  return `${said} · pull to refresh`;
});
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// --- the list -------------------------------------------------------------------------
const shownCount = computed(() => askView.batches.reduce((n, b) => n + b.results.length, 0));
const canMore = computed(() => hasAskBoard.value && !askView.legacy && !askView.exhausted && shownCount.value > 0);
const countLine = computed(() => {
  if (askView.legacy) return askView.interpretation;
  const n = askView.matching;
  if (!n) return shownCount.value ? `${shownCount.value} multi${shownCount.value === 1 ? '' : 's'}` : 'No multis fit yet';
  return `${matchCount(n)} multi${n === 1 ? '' : 's'} match`;
});
const endLine = computed(() => (shownCount.value === 1
  ? 'That is the only multi this ask makes on the current prices.'
  : `That's all ${shownCount.value} different multis this ask makes on the current prices.`));
function more() {
  another(PAGE);
}
const sentinel = ref(null);
let observer = null;
onMounted(() => {
  if (typeof IntersectionObserver !== 'function') return;
  observer = new IntersectionObserver((entries) => {
    if (entries.some((e) => e.isIntersecting) && canMore.value) more();
  }, { rootMargin: '400px 0px' });
  if (sentinel.value) observer.observe(sentinel.value);
});
// The sentinel exists once the first page does; observed whenever it appears.
watch(sentinel, (el, old) => {
  if (old) observer?.unobserve(old);
  if (el) observer?.observe(el);
});
onBeforeUnmount(() => observer?.disconnect());

// --- swapping a leg ---------------------------------------------------------------------
const swapping = ref(null);
function startSwap(ticket, legKey) {
  if (swapping.value?.ticket.multi.id === ticket.multi.id && swapping.value.legKey === legKey) {
    swapping.value = null;
    return;
  }
  const { options, unsatisfied } = swapOptions(ticket, legKey);
  swapping.value = { ticket, legKey, options, unsatisfied };
}
const swapView = computed(() => {
  const s = swapping.value;
  if (!s) return { options: [], empty: '', newLeg: (o) => o.legs[0] };
  return {
    options: s.options,
    empty: s.unsatisfied ? `${cap(s.unsatisfied.reasons[0])}.` : 'Nothing else fits beside the other legs.',
    newLeg: (o) => o.legs.find((l) => !s.ticket.legs.some((x) => x.key === l.key)) ?? o.legs[0],
  };
});
function useSwap(o) {
  applySwap(swapping.value.ticket, o);
  swapping.value = null;
}

// --- sharing a few -------------------------------------------------------------------
// The most one picture holds; the picker stops here and says so.
const MAX_CHOSEN = 10;
const askingShare = ref(false);
const selecting = ref(false);
const chosenIds = ref(new Set());
/** Every multi on the list, in the order the screen has them. */
const listed = computed(() => askView.batches.flatMap((b) => b.results));
/** The chosen, in list order rather than tap order: a picture reads top down. */
const chosen = computed(() => listed.value.filter((t) => chosenIds.value.has(t.multi.id)));
const isChosen = (t) => chosenIds.value.has(t.multi.id);
const atCap = computed(() => chosen.value.length >= MAX_CHOSEN);
const chooseLine = computed(() => {
  if (atCap.value) return `That's ${MAX_CHOSEN}, the most one picture holds.`;
  const n = chosen.value.length;
  return n ? `${n} chosen` : 'Tap the multis to share';
});
function toggleChosen(t) {
  const next = new Set(chosenIds.value);
  if (next.has(t.multi.id)) next.delete(t.multi.id);
  else if (next.size < MAX_CHOSEN) next.add(t.multi.id);
  chosenIds.value = next;
}
function stopChoosing() {
  selecting.value = false;
  chosenIds.value = new Set();
}
function shareTop(n) {
  const tickets = listed.value.slice(0, n);
  if (tickets.length) emit('share-tickets', tickets);
}
function shareChosen() {
  if (!chosen.value.length) return;
  emit('share-tickets', chosen.value);
  stopChoosing();
}
const shareOne = (t) => emit('share-tickets', [t]);
const shareButtons = computed(() => [
  { text: 'Share top 3', handler: () => { shareTop(3); } },
  { text: 'Share top 5', handler: () => { shareTop(5); } },
  { text: 'Choose tickets…', handler: () => { chosenIds.value = new Set(); selecting.value = true; } },
  { text: 'Cancel', role: 'cancel' },
]);
// A new ask is a new list: nothing chosen off the old one survives it.
watch(() => askView.interpretation, () => { if (selecting.value) stopChoosing(); });

const showHistory = ref(false);
const historyCount = computed(() => historyToday.value.reduce((n, h) => n + h.tickets.length, 0));

onMounted(() => {
  scheduleAsk();
  viewed();
});
</script>

<style scoped>
/* At least a screen tall from the start. The tickets arrive a beat after the
   first paint, and with nothing reserving their room the Gambling Help footer
   under Today was drawn near the top of the screen and then pushed down twice:
   CLS 0.21 on the app shell's Lighthouse run, all of it that footer. */
.ask {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  min-height: 100vh;
  min-height: 100dvh;
}
:where(.ask) p,
:where(.ask) h2,
:where(.ask) ul {
  margin: 0;
}

/* Text buttons: the tint, no box, a full 44px target. */
.tbtn {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--accent);
  font: 400 var(--fs-body)/1.2 var(--font-body);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.tbtn:disabled {
  opacity: 0.4;
  cursor: default;
}
.tbtn--quiet {
  color: var(--carbon-faint);
}

/* --- the ask card ------------------------------------------------------------- */
.ask-card {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px 0 12px;
  box-shadow: none;
}
.ask-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
  padding: 0 16px;
}
.ask-said {
  min-width: 0;
  font: 400 0.9375rem/1.3 var(--font-body);
  color: var(--carbon);
}
.ask-line .tbtn {
  flex: 0 0 auto;
}
.ask-note {
  padding: 0 16px 4px;
  font: 400 0.8125rem/1.3 var(--font-body);
  color: var(--carbon-faint);
}
.capsules {
  display: flex;
  gap: var(--sp-2);
  overflow-x: auto;
  padding: 0 16px;
  scrollbar-width: none;
}
.capsules::-webkit-scrollbar {
  display: none;
}
.capsule {
  flex: 0 0 auto;
  min-height: 44px;
  padding: 0 16px;
  border: 0;
  border-radius: 999px;
  background: var(--fill, var(--rule));
  color: var(--carbon);
  font: 600 0.875rem/1 var(--font-body);
  white-space: nowrap;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.capsule[aria-pressed='true'] {
  background: var(--accent);
  color: var(--sheet);
}
.capsule--off {
  text-decoration: line-through;
  color: var(--carbon-faint);
}
.capsule--more {
  background: transparent;
  color: var(--accent);
  padding: 0 8px;
}

/* --- the list header ------------------------------------------------------------ */
.list-head {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 4px;
}
.list-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
}
.count {
  font: 700 var(--fs-body)/1.25 var(--font-body);
  font-variant-numeric: tabular-nums;
  color: var(--carbon);
}
.fresh {
  font: 400 0.8125rem/1.3 var(--font-body);
  color: var(--carbon-faint);
}
.is-stale {
  color: var(--tier-medium);
}

/* --- choosing what to share ------------------------------------------------------- */
.select-bar {
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
  margin: 0 calc(-1 * var(--sp-2));
  padding: 8px 12px calc(8px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--rule);
  background: var(--sheet);
}
.select-note {
  min-width: 0;
  font: 400 0.8125rem/1.3 var(--font-body);
  color: var(--carbon-faint);
}
.select-actions {
  display: flex;
  align-items: center;
  gap: var(--sp-4);
  flex: 0 0 auto;
}
.primary {
  min-height: 44px;
  padding: 0 20px;
  border: 0;
  border-radius: var(--r-action);
  background: var(--accent);
  color: var(--sheet);
  font: 600 var(--fs-body)/1.2 var(--font-body);
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.primary:disabled {
  opacity: 0.4;
  cursor: default;
}
.primary:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

/* --- unsatisfied ------------------------------------------------------------------ */
.unsatisfied {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  box-shadow: none;
}
.unsatisfied-why {
  font: 600 0.9375rem/1.4 var(--font-body);
  color: var(--carbon);
}
.unsatisfied-try,
.unsatisfied-after {
  font: 400 0.8125rem/1.3 var(--font-body);
  color: var(--carbon-faint);
}
.loosens {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-2);
}

/* --- the list ----------------------------------------------------------------------- */
.answers {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  margin: 0;
  padding: 0;
  list-style: none;
}
.more {
  display: flex;
  justify-content: center;
}
.foot-line {
  font: 400 0.8125rem/1.35 var(--font-body);
  color: var(--carbon-faint);
}
.centred {
  text-align: center;
}

/* --- the foot: pinned, daily asks, shown today ------------------------------------------ */
.kept {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--sp-2);
  margin-top: var(--sp-3);
}
.kept-head {
  padding: 0 4px;
  font: 400 0.8125rem/1.3 var(--font-body);
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--carbon-faint);
}
.kept-rows {
  width: 100%;
  padding: 0;
  list-style: none;
  border-radius: var(--r-sheet);
  background: var(--sheet);
}
.kept-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
  padding: 0 16px;
}
.kept-row + .kept-row::before {
  content: '';
  position: absolute;
  top: 0;
  left: 16px;
  right: 0;
  border-top: 1px solid var(--rule);
}
.kept-name {
  min-width: 0;
  text-align: left;
}
.history-entry {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  width: 100%;
}
/* At the foot of the (screen-tall) answer, not straight after its content:
   straight after, it was drawn high while the list was empty and then pushed
   down when the tickets arrived. */
.how {
  display: flex;
  justify-content: center;
  margin-top: auto;
}
.tbtn:focus-visible,
.capsule:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
</style>
